import PageHeader from "components/PageHeader/PageHeader";
import SafeImage from "components/SafeImage/SafeImage";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { getClients } from "data/api";
import { BuildingOffice2Icon, MapPinIcon, SparklesIcon } from "@heroicons/react/24/outline";

interface ClientItem {
  id: number | string;
  name: string;
  image: string;
  location?: string;
}

const FEATURED_GRADIENTS = [
  "from-rose-600 via-red-600 to-orange-500",
  "from-blue-700 via-indigo-600 to-violet-600",
  "from-emerald-600 via-teal-600 to-cyan-500",
  "from-amber-500 via-orange-500 to-red-500",
  "from-slate-700 via-blue-800 to-indigo-900",
  "from-fuchsia-600 via-purple-600 to-indigo-600",
];

const ClientCard: FC<{ client: ClientItem; index: number }> = ({
  client,
  index,
}) => {
  const gradient = FEATURED_GRADIENTS[index % FEATURED_GRADIENTS.length];

  return (
    <article className="group relative flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-700/80 shadow-bcr-card hover:shadow-bcr-card-hover transition-all duration-300 hover:-translate-y-1">
      <div
        className={`relative h-28 sm:h-32 bg-gradient-to-br ${gradient} shrink-0`}
      >
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_20%,white,transparent_55%)]" />
        <div className="absolute bottom-3 left-4 right-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur-sm border border-white/25">
            <SparklesIcon className="w-3 h-3" />
            Verified partner
          </span>
        </div>
      </div>

      <div className="relative flex justify-center -mt-14 px-5">
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white dark:bg-neutral-800 border-4 border-white dark:border-neutral-900 shadow-xl flex items-center justify-center p-3 group-hover:scale-105 transition-transform duration-300">
          <SafeImage
            src={client.image}
            alt={client.name}
            placeholderVariant="logo"
            placeholderCompact
            objectFit="contain"
            className="w-full h-full"
            imgClassName="max-w-full max-h-full object-contain"
          />
        </div>
      </div>

      <div className="flex flex-col flex-1 px-5 sm:px-6 pt-4 pb-6 text-center">
        <h3 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          {client.name}
        </h3>
        {client.location && (
          <p className="mt-1.5 inline-flex items-center justify-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
            <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
            {client.location}
          </p>
        )}
        <p className="mt-3 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed line-clamp-2">
          Supplying expansion joints, tile trims & architectural profiles on
          landmark developments.
        </p>
      </div>
    </article>
  );
};

export interface ListingExperiencesPageProps {
  className?: string;
}

const ClientsPage: FC<ListingExperiencesPageProps> = ({ className = "" }) => {
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getClients()
      .then((items) => {
        if (Array.isArray(items)) {
          setClients(
            items.map((item: ClientItem) => ({
              id: item.id,
              name: item.name,
              image: item.image,
              location: item.location,
            }))
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className={`nc-ClientsPage relative overflow-hidden ${className}`}
      data-nc-id="ClientsPage"
    >
      <Helmet>
        <title>Our Clients | BCR LTD. UK</title>
        <meta
          name="description"
          content="BCR Ltd is trusted by leading developers and contractors across the UAE and GCC. View our client portfolio and partner with us for your next project."
        />
      </Helmet>

      <PageHeader
        title="Our Clients"
        subtitle="Trusted by the region's most respected developers and contractors — from iconic towers to large-scale communities."
      />

      <div className="container relative px-4 sm:px-6 py-10 sm:py-12 pb-16 sm:pb-24">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              >
                <div className="h-28 bg-neutral-200 dark:bg-neutral-700" />
                <div className="p-6 flex flex-col items-center">
                  <div className="w-24 h-24 -mt-14 rounded-2xl bg-neutral-300 dark:bg-neutral-600" />
                  <div className="mt-6 h-4 w-2/3 rounded bg-neutral-200 dark:bg-neutral-700" />
                </div>
              </div>
            ))}
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40">
            <BuildingOffice2Icon className="w-12 h-12 mx-auto text-neutral-400" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
              No clients found
            </h3>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {clients.map((client, index) => (
              <ClientCard key={client.id} client={client} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
